/**
 * Componente Badge Corporativo
 * Badge reutilizable con estilos corporativos mejorados
 * Incluye variantes para roles, estados, verificación, etc.
 */

import React from 'react';
import { Chip, ChipProps, useTheme, alpha } from '@mui/material';
import {
  CheckCircle,
  Cancel,
  AdminPanelSettings,
  Person,
  Shield,
  VerifiedUser,
  Warning,
  Image as ImageIcon,
} from '@mui/icons-material';

export type BadgeVariant = 
  | 'role'
  | 'status'
  | 'verified'
  | 'system'
  | 'custom'
  | 'image';

export type BadgeType = 
  | 'role-admin'
  | 'role-super_admin'
  | 'role-user'
  | 'role-custom'
  | 'status-active'
  | 'status-inactive'
  | 'verified-yes'
  | 'verified-no'
  | 'system-yes'
  | 'system-no'
  | 'custom-primary'
  | 'custom-success'
  | 'custom-warning'
  | 'custom-error'
  | 'custom-info'
  | 'image';

interface CorporateBadgeProps extends Omit<ChipProps, 'color' | 'variant'> {
  /**
   * Variante del badge (rol, estado, verificado, etc.)
   */
  variant?: BadgeVariant;
  
  /**
   * Tipo específico del badge para aplicar estilos automáticos
   */
  type?: BadgeType;
  
  /**
   * Si es true, muestra un icono automático según el tipo
   */
  showIcon?: boolean;
  
  /**
   * Si es true, aplica un efecto de gradiente sutil
   */
  gradient?: boolean;
  
  /**
   * Si es true, aplica una sombra suave
   */
  shadow?: boolean;
}

/**
 * Obtiene el icono apropiado según el tipo de badge
 */
const getIconForType = (type?: BadgeType): React.ReactElement | undefined => {
  if (!type) return undefined;
  
  const iconProps = { fontSize: 'small' as const };
  
  switch (type) {
    case 'role-admin':
    case 'role-super_admin':
      return <AdminPanelSettings {...iconProps} />;
    case 'role-user':
      return <Person {...iconProps} />;
    case 'status-active':
      return <CheckCircle {...iconProps} />;
    case 'status-inactive':
      return <Cancel {...iconProps} />;
    case 'verified-yes':
      return <VerifiedUser {...iconProps} />;
    case 'verified-no':
      return <Warning {...iconProps} />;
    case 'system-yes':
      return <Shield {...iconProps} />;
    case 'image':
      return <ImageIcon {...iconProps} />;
    default:
      return undefined;
  }
};

/**
 * Obtiene los estilos según el tipo de badge
 */
const getStylesForType = (
  type: BadgeType,
  theme: any,
  gradient?: boolean,
  shadow?: boolean
) => {
  const baseStyles: any = {
    fontWeight: 500,
    fontSize: '0.6rem',
    height: '19.2px',
    borderRadius: theme.shape.borderRadius * 2,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  };

  // Aplicar sombra si está habilitada
  if (shadow) {
    baseStyles.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
    baseStyles['&:hover'].boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
  }

  switch (type) {
    case 'role-admin':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.accent.light} 0%, ${theme.custom.colors.badge.accent.vibrant} 100%)`
          : theme.custom.colors.badge.accent.vibrant,
        color: theme.custom.colors.badge.accent.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.accent.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'role-super_admin':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.accentSecondary.light} 0%, ${theme.custom.colors.badge.accentSecondary.vibrant} 100%)`
          : theme.custom.colors.badge.accentSecondary.vibrant,
        color: theme.custom.colors.badge.accentSecondary.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.accentSecondary.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'role-user':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.info.light} 0%, ${theme.custom.colors.badge.info.vibrant} 100%)`
          : theme.custom.colors.badge.info.vibrant,
        color: theme.custom.colors.badge.info.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.info.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'role-custom':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.info.light} 0%, ${theme.custom.colors.badge.info.main} 100%)`
          : theme.custom.colors.badge.info.main,
        color: theme.custom.colors.badge.info.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.info.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'status-active':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.success.light} 0%, ${theme.custom.colors.badge.success.vibrant} 100%)`
          : theme.custom.colors.badge.success.vibrant,
        color: theme.custom.colors.badge.success.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.success.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'status-inactive':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.error.light} 0%, ${theme.custom.colors.badge.error.vibrant} 100%)`
          : theme.custom.colors.badge.error.vibrant,
        color: theme.custom.colors.badge.error.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.error.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'verified-yes':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.success.light} 0%, ${theme.custom.colors.badge.success.vibrant} 100%)`
          : theme.custom.colors.badge.success.vibrant,
        color: theme.custom.colors.badge.success.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.success.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'verified-no':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.warning.light} 0%, ${theme.custom.colors.badge.warning.vibrant} 100%)`
          : theme.custom.colors.badge.warning.vibrant,
        color: theme.custom.colors.badge.warning.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.warning.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'system-yes':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.info.light} 0%, ${theme.custom.colors.badge.info.main} 100%)`
          : theme.custom.colors.badge.info.main,
        color: theme.custom.colors.badge.info.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.info.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'system-no':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.accent.light} 0%, ${theme.custom.colors.badge.accent.vibrant} 100%)`
          : theme.custom.colors.badge.accent.vibrant,
        color: theme.custom.colors.badge.accent.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.accent.main, 0.2)}`,
        fontWeight: 600, // Texto más pesado para mejor legibilidad
      };
    
    case 'custom-primary':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.info.light} 0%, ${theme.custom.colors.badge.info.main} 100%)`
          : theme.custom.colors.badge.info.main,
        color: theme.custom.colors.badge.info.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.info.main, 0.2)}`,
        fontWeight: 600,
      };
    
    case 'custom-success':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.success.light} 0%, ${theme.custom.colors.badge.success.vibrant} 100%)`
          : theme.custom.colors.badge.success.vibrant,
        color: theme.custom.colors.badge.success.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.success.main, 0.2)}`,
        fontWeight: 600,
      };
    
    case 'custom-warning':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.warning.light} 0%, ${theme.custom.colors.badge.warning.vibrant} 100%)`
          : theme.custom.colors.badge.warning.vibrant,
        color: theme.custom.colors.badge.warning.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.warning.main, 0.2)}`,
        fontWeight: 600,
      };
    
    case 'custom-error':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.error.light} 0%, ${theme.custom.colors.badge.error.vibrant} 100%)`
          : theme.custom.colors.badge.error.vibrant,
        color: theme.custom.colors.badge.error.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.error.main, 0.2)}`,
        fontWeight: 600,
      };
    
    case 'custom-info':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.accent.light} 0%, ${theme.custom.colors.badge.accent.vibrant} 100%)`
          : theme.custom.colors.badge.accent.vibrant,
        color: theme.custom.colors.badge.accent.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.accent.main, 0.2)}`,
        fontWeight: 600,
      };
    
    case 'image':
      return {
        ...baseStyles,
        backgroundColor: gradient
          ? `linear-gradient(135deg, ${theme.custom.colors.badge.info.light} 0%, ${theme.custom.colors.badge.info.main} 100%)`
          : theme.custom.colors.badge.info.main,
        color: theme.custom.colors.badge.info.contrastText,
        border: `1px solid ${alpha(theme.custom.colors.badge.info.main, 0.2)}`,
        fontWeight: 500,
      };
    
    default:
      return baseStyles;
  }
};

export const CorporateBadge: React.FC<CorporateBadgeProps> = ({
  variant,
  type,
  showIcon = true,
  gradient = true,
  shadow = true,
  icon,
  label,
  sx,
  ...props
}) => {
  const theme = useTheme();
  
  // Determinar el tipo automáticamente si no se proporciona
  let badgeType: BadgeType | undefined = type;
  
  if (!badgeType && variant) {
    // Inferir tipo desde la variante y el label
    const labelStr = String(label || '').toLowerCase();
    
    if (variant === 'role') {
      if (labelStr.includes('admin') || labelStr.includes('super')) {
        badgeType = labelStr.includes('super') ? 'role-super_admin' : 'role-admin';
      } else {
        badgeType = 'role-user';
      }
    } else if (variant === 'status') {
      badgeType = labelStr.includes('activo') || labelStr.includes('active') 
        ? 'status-active' 
        : 'status-inactive';
    } else if (variant === 'verified') {
      badgeType = labelStr.includes('sí') || labelStr.includes('yes') || labelStr === 'true'
        ? 'verified-yes'
        : 'verified-no';
    } else if (variant === 'system') {
      badgeType = labelStr.includes('sistema') || labelStr.includes('system')
        ? 'system-yes'
        : 'system-no';
    } else if (variant === 'image') {
      badgeType = 'image';
    }
  }
  
  // Obtener icono
  const badgeIcon = icon !== undefined 
    ? icon 
    : (showIcon && badgeType ? getIconForType(badgeType) : undefined);
  
  // Obtener estilos
  const badgeStyles = badgeType 
    ? getStylesForType(badgeType, theme, gradient, shadow)
    : {
        fontWeight: 500,
        fontSize: '0.6rem',
        height: '19.2px',
        borderRadius: theme.shape.borderRadius * 2,
      };
  
  return (
    <Chip
      {...props}
      icon={badgeIcon}
      label={label}
      size="small"
      variant="filled"
      // No pasar la prop 'color' para evitar conflictos
      sx={{
        // Aplicar estilos base primero
        ...badgeStyles,
        // Asegurar que el backgroundColor se aplique correctamente
        // Incluyendo gradientes
        background: badgeStyles.backgroundColor,
        backgroundColor: typeof badgeStyles.backgroundColor === 'string' && badgeStyles.backgroundColor.includes('gradient')
          ? undefined // Si es gradiente, usar 'background' en lugar de 'backgroundColor'
          : badgeStyles.backgroundColor,
        // Asegurar que los estilos se apliquen en todas las variantes
        '&.MuiChip-root': {
          background: badgeStyles.backgroundColor,
          backgroundColor: typeof badgeStyles.backgroundColor === 'string' && badgeStyles.backgroundColor.includes('gradient')
            ? undefined
            : badgeStyles.backgroundColor,
          color: badgeStyles.color,
          border: badgeStyles.border,
        },
        '&.MuiChip-filled': {
          background: badgeStyles.backgroundColor,
          backgroundColor: typeof badgeStyles.backgroundColor === 'string' && badgeStyles.backgroundColor.includes('gradient')
            ? undefined
            : badgeStyles.backgroundColor,
          color: badgeStyles.color,
          border: badgeStyles.border,
        },
        // Asegurar que el texto y el icono tengan el color correcto
        '& .MuiChip-label': {
          color: badgeStyles.color,
        },
        '& .MuiChip-icon': {
          color: badgeStyles.color,
        },
        // Aplicar estilos personalizados adicionales
        ...sx,
      }}
    />
  );
};

